













































































































































































































































































































import { AccessLevelDynamicBadge, ConfirmButton, CopyToClipboardButton } from '@/app/components';
import { useAxios, useFeatureFlags, useFilters } from '@/app/composable';
import store from '@/app/store';
import { WorkflowStatus } from '@/modules/apollo/constants';
import { AssetTypeId } from '@/modules/asset/constants';
import {
    BellIcon,
    ChipIcon,
    ClockIcon,
    CogIcon,
    CubeTransparentIcon,
    DatabaseIcon,
    DuplicateIcon,
    MinusIcon,
    PencilAltIcon,
    PlayIcon,
    TrashIcon,
    XCircleIcon,
} from '@vue-hero-icons/outline';
import { CalendarIcon, ChartBarIcon, ExclamationCircleIcon, UserIcon } from '@vue-hero-icons/solid';
import { PropType, computed, defineComponent, ref } from '@vue/composition-api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import * as R from 'ramda';
import { WorkflowAPI } from '../api';
import { ExecutionFrameworkWrapper, ExecutionStatus } from '../constants';

dayjs.extend(weekOfYear);
dayjs.extend(utc);

export default defineComponent({
    name: 'WorkflowOverview',
    props: {
        workflow: {
            type: Object,
            required: true,
        },
        alert: {
            type: Object as PropType<{ entityId: string | number; alertId: string; enabled: boolean }>,
            required: false,
        },
    },
    components: {
        ChartBarIcon,
        DuplicateIcon,
        PencilAltIcon,
        ClockIcon,
        TrashIcon,
        AccessLevelDynamicBadge,
        UserIcon,
        CalendarIcon,
        CogIcon,
        ChipIcon,
        CubeTransparentIcon,
        DatabaseIcon,
        CopyToClipboardButton,
        PlayIcon,
        BellIcon,
        MinusIcon,
        XCircleIcon,
        ConfirmButton,
        ExclamationCircleIcon,
    },
    setup(props: any, { emit, root }: { emit: any; root: any }) {
        const provenanceTypeStatusClass = {
            Input: {
                colour: 'text-blue-700',
                label: 'Input',
            },
            Output: {
                colour: 'text-green-700',
                label: 'Output',
            },
            Both: {
                colour: 'text-orange-800',
                label: 'Input and Output',
            },
        };

        const assetTypeClass = {
            [AssetTypeId.Model]: 'text-cherry-700',
            [AssetTypeId.Result]: 'text-blue-800',
            [AssetTypeId.Dataset]: 'text-orange-800',
        };

        const { formatDate, formatDateTime } = useFilters();
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');
        const isExpanded = ref<boolean>(false);
        const { reverse } = R;
        const disabledCanceling = ref<boolean>(false);
        const { exec } = useAxios(true);

        const hasVisualisations = computed(() => props.workflow.visualisations.length > 0);

        const toggleExpand = () => {
            if (hasVisualisations.value) {
                isExpanded.value = !isExpanded.value;
            }
        };

        const cancelExecution = (workflowId: string) => {
            exec(WorkflowAPI.cancelActiveExecutions(workflowId))
                .then(() => {
                    (root as any).$toastr.s(
                        `Active executions are cancelling. Please note that this operation may need some time to finish`,
                        'Success',
                    );
                })
                .catch(() => {
                    (root as any).$toastr.e(`Cancel active executions has been failed`, 'Error');
                });
        };

        const createdAtShort = computed(() => {
            return dayjs(props.workflow.createdAt).utc().fromNow();
        });

        const createFullDatetime = (date: Date) => {
            return dayjs(date).utc().format('DD/MM/YYYY HH:mm');
        };

        const createdAtFull = computed(() => createFullDatetime(props.workflow.createdAt));
        const user = computed(() => store.state.auth.user);
        const isUserWorkflowCreator = computed(() => user.value.id === Number(props.workflow?.createdBy?.id));
        const frameworkImagePath = computed(() => {
            const framework = ExecutionFrameworkWrapper.find(props.workflow.framework);
            if (framework) {
                return framework.imagePath;
            }
            return null;
        });

        const openWorkflow = () => {
            emit('open', props.workflow);
        };

        const configureWorkflow = () => {
            emit('configure', props.workflow);
        };

        const deleteWorkflow = () => {
            emit('delete', props.workflow);
        };

        const historyWorkflow = () => {
            emit('history', props.workflow);
        };

        const editWorkflow = () => emit('edit', props.workflow);

        const cloneWorkflow = () => emit('clone', props.workflow);

        const viewWorkflow = () => emit('view', props.workflow);

        const runWorkflow = () => emit('run', props.workflow);

        const workflowStatus = computed(() => {
            switch (props.workflow.executionStatus) {
                case ExecutionStatus.Completed:
                    return { class: 'text-white bg-green-800', label: 'Completed' };
                case ExecutionStatus.Draft:
                    return { class: 'text-white bg-slateblue-700', label: 'Configuration' };
                case ExecutionStatus.Failed:
                    return { class: 'text-white bg-red-700', label: 'Failed' };
                case ExecutionStatus.Running:
                    return { class: 'text-white bg-slate-700', label: 'Running' };
                case ExecutionStatus.Skipped:
                    return { class: 'text-white bg-neutral-600', label: 'Skipped' };
                case ExecutionStatus.Ready:
                    return { class: 'text-white bg-teal-600', label: 'Execution Pending' };
                case ExecutionStatus.Queued:
                    return { class: 'text-white bg-slate-500', label: 'Queued' };
                case ExecutionStatus.Updating:
                    return { class: 'text-white bg-slate-600', label: 'Update' };
                case ExecutionStatus.Cancelled:
                    return { class: 'text-white bg-orange-800', label: 'Cancelled' };
                case ExecutionStatus.Deprecated:
                    return { class: 'text-white bg-cherry-900', label: 'Deprecated' };
                case ExecutionStatus.Suspended:
                    return { class: 'text-white bg-red-500', label: 'Suspended' };
                default:
                    return { class: 'text-white bg-danger-500', label: 'N/A' };
            }
        });

        const filteredProvenanceAssets = computed(() => {
            const provenanceAssets: any = [];
            const filteredIds: number[] = [];

            props.workflow.inputAssets?.forEach((inputAsset: any) => {
                filteredIds.push(inputAsset.id);
                provenanceAssets.push({
                    ...inputAsset,
                    type: props.workflow.provenanceAssetIds.includes(inputAsset.id) ? 'Both' : 'Input',
                });
            });

            // add the rest of the assets in the provenance assets if they don't already exist
            props.workflow.provenanceAssets?.forEach((outputAsset: any) => {
                if (!filteredIds.includes(outputAsset.id)) {
                    filteredIds.push(outputAsset.id);
                    provenanceAssets.push({ ...outputAsset, type: 'Output' });
                }
            });
            return provenanceAssets;
        });

        const editAlert = () => {
            if (props.alert)
                root.$router.push({ name: 'alerts:edit', params: { id: props.alert.alertId, backTo: 'workflows' } });
        };

        return {
            isExpanded,
            createdAtFull,
            createdAtShort,
            WorkflowStatus,
            ExecutionStatus,
            hasVisualisations,
            frameworkImagePath,
            reverse,
            openWorkflow,
            toggleExpand,
            deleteWorkflow,
            historyWorkflow,
            createFullDatetime,
            configureWorkflow,
            editWorkflow,
            cloneWorkflow,
            viewWorkflow,
            formatDate,
            formatDateTime,
            cancelExecution,
            disabledCanceling,
            dayjs,
            AssetTypeId,
            workflowStatus,
            filteredProvenanceAssets,
            runWorkflow,
            isUserWorkflowCreator,
            provenanceTypeStatusClass,
            isAlertsEnabled,
            editAlert,
            assetTypeClass,
        };
    },
});
